import { Check } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@repo/utils";
import { ExplorerDirectoryList } from "./directory-list-format/explorer-directory-list";
import { MacOSDirectoryList } from "./directory-list-format/macos-directory-list";
import { DirectoryNav } from "./directory-nav";
import { DirectoryTrigger } from "./directory-trigger";
import type { DirectoryColumn, FsResponse } from "./types";
import { Button } from "../../button/button";
import { Dialog } from "../../dialog/dialog";
import { DialogContent } from "../../dialog/sections/dialog-content";
import { DialogHeader } from "../../dialog/sections/dialog-header";
import { DialogTitle } from "../../dialog/sections/dialog-title";
import type { Props as InputProps } from "../input";

const VOLUMES_PATH = "/Volumes";
const ROOT_PATH = "/";

const joinPath = (parent: string, name: string) =>
  parent === ROOT_PATH ? `/${name}` : `${parent}/${name}`;

const getPathChain = (target: string) =>
  target
    .split("/")
    .filter(Boolean)
    .reduce<string[]>(
      (chain, segment) => [...chain, joinPath(chain.at(-1) ?? ROOT_PATH, segment)],
      [ROOT_PATH]
    );

export type DirectoryInputProps = Omit<InputProps, "onChange" | "value"> & {
  value?: string | null;
  onChange?: (path: string | null) => void;
  defaultPath?: string;
  layout?: "columns" | "list";
  apiBaseUrl: string;
};

export const DirectoryInput = ({
  className,
  variant,
  inputSize,
  value,
  onChange,
  defaultPath,
  disabled,
  label,
  error,
  helperText,
  placeholder = "Select a folder...",
  layout = "columns",
  apiBaseUrl,
  ...props
}: DirectoryInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [columns, setColumns] = useState<DirectoryColumn[]>([]);
  const [fetchError, setFetchError] = useState<string>();
  const requestIdRef = useRef(0);

  const fetchDir = useCallback(
    async (path: string): Promise<DirectoryColumn | null> => {
      const res = await fetch(`${apiBaseUrl}/fs?path=${encodeURIComponent(path)}`);
      if (res.status === 400 || res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to load directory");
      const data: FsResponse = await res.json();
      return { path: data.currentPath, folders: data.folders, error: data.error };
    },
    [apiBaseUrl]
  );

  const openPath = useCallback(
    async (target?: string | null) => {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      setFetchError(undefined);
      setColumns([]);
      try {
        for (const candidate of [target, defaultPath, VOLUMES_PATH, ROOT_PATH]) {
          if (!candidate) continue;
          const result = await Promise.all(getPathChain(candidate).map(fetchDir));
          if (requestId !== requestIdRef.current) return;
          if (result.every((c): c is DirectoryColumn => c !== null)) {
            setColumns(result);
            return;
          }
        }
      } catch (e) {
        console.error("Directory fetch error:", e);
        if (requestId === requestIdRef.current) setFetchError("Could not connect to GXE Bridge.");
      } finally {
        if (requestId === requestIdRef.current) setIsLoading(false);
      }
    },
    [defaultPath, fetchDir]
  );

  const handleFolderClick = async (columnIndex: number, folderName: string) => {
    const parent = columns[columnIndex];
    if (!parent) return;
    const path = joinPath(parent.path, folderName);
    const kept = columns.slice(0, columnIndex + 1);
    const requestId = ++requestIdRef.current;
    setColumns(kept);
    setIsLoading(true);
    try {
      const next = await fetchDir(path);
      if (requestId !== requestIdRef.current) return;
      setColumns([...kept, next ?? { path, folders: [], error: "Directory not found" }]);
    } catch (e) {
      console.error("Directory fetch error:", e);
      if (requestId === requestIdRef.current) setFetchError("Could not connect to GXE Bridge.");
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) openPath(value);
  }, [isOpen, openPath, value]);

  const selectedPath = columns.at(-1)?.path;

  const handleSelect = () => {
    if (selectedPath && onChange) {
      onChange(selectedPath);
      setIsOpen(false);
    }
  };

  return (
    <div className={cn("w-full", props.containerClassName)}>
      {label && (
        <label className="mb-2 block text-[12px] font-medium">
          {label}
          {props.required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      <DirectoryTrigger
        value={value}
        placeholder={placeholder}
        onClick={() => !disabled && setIsOpen(true)}
        onClear={() => onChange?.(null)}
        disabled={disabled}
        error={error}
        variant={variant}
        inputSize={inputSize}
        className={className}
      />
      {error && <p className="mt-1.5 text-[12px] text-red-500">{error}</p>}
      {!error && helperText && <p className="mt-1.5 text-[12px] text-gray-500">{helperText}</p>}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent allowNavigation className="max-w-4xl gap-0 overflow-hidden p-0">
          <DialogHeader className="border-b bg-gray-50/50 px-4 py-3">
            <DialogTitle>Select Directory</DialogTitle>
          </DialogHeader>

          <div className="flex h-[60vh] min-h-[400px] flex-col">
            <DirectoryNav
              currentPath={selectedPath}
              canGoUp={columns.length > 1}
              isLoading={isLoading}
              onGoUp={() => setColumns((prev) => prev.slice(0, -1))}
              onGoHome={() => openPath()}
            />

            <div className="flex min-h-0 flex-1 bg-white">
              {layout === "columns" ? (
                <MacOSDirectoryList
                  columns={columns}
                  isLoading={isLoading}
                  error={fetchError}
                  onFolderClick={handleFolderClick}
                />
              ) : (
                <ExplorerDirectoryList
                  isLoading={isLoading}
                  error={fetchError ?? columns.at(-1)?.error}
                  folders={columns.at(-1)?.folders}
                  onFolderClick={(name) => handleFolderClick(columns.length - 1, name)}
                />
              )}
            </div>
            <div className="flex justify-end gap-2 border-t bg-gray-50 p-4">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSelect}
                disabled={!selectedPath || isLoading || !!fetchError}
                className="gap-2"
              >
                <Check className="h-4 w-4" />
                Select Current Folder
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
